import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { StammkundeService } from '../../../Services/stammkunde.service';
import {
  Auftrag,
  AuftragAssignment,
  AUFTRAG_STATUS_LABELS,
  AUFTRAG_STATUS_ORDER,
  AuftragStatus,
  EinsatzMaterial,
  MATERIAL_STATUS_LABELS,
  MyStammkundeMembership,
  PlannableUser,
  StammkundeWorker,
  TakedaPricing,
  TakedaPricingMaterialZeile,
  TakedaProjektDaten,
  TakedaProjektEinsatzMaterial,
  TakedaProjektFreiesMaterial,
  TakedaProjektLvMaterial,
  TakedaTagesberichtLvPosition,
  TIME_ENTRY_STATUS_LABELS,
  ZeitEintrag
} from '../../../Models/stammkunde.model';
import { AuthService } from '../../../api-authorization/authorize.service';
import { AuftragFormComponent } from '../auftrag-form/auftrag-form.component';
import { EinsatzFormComponent } from '../einsatz-form/einsatz-form.component';
import { ZeitEintragFormComponent, ZeitPerson } from '../zeit-eintrag-form/zeit-eintrag-form.component';
import { MaterialErfassungComponent } from '../material-erfassung/material-erfassung.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { buildTakedaAbrechnungPdf, takedaExportDateiname } from '../shared/takeda-abrechnung-pdf.util';
import { speichereTakedaExportDatei, takedaExportFehlerText } from '../shared/takeda-export-download.util';
import { TakedaExcelExportDialogComponent, TakedaExcelExportDialogData } from '../shared/takeda-excel-export-dialog/takeda-excel-export-dialog.component';

interface DayGroup {
  day: Date;
  items: AuftragAssignment[];
}

/**
 * Stunden eines Einsatztages für die Takeda-Ansicht "Nach Einsatz".
 * Die Zeilen bleiben die einzelnen Zeiteinträge, damit Status-Chips und
 * Freigabe-/Bearbeiten-Aktionen unverändert erhalten bleiben.
 */
interface TimeEinsatzGroup {
  key: string;
  assignmentId: number | null;
  title: string | null;
  day: Date;
  items: ZeitEintrag[];
  hours: number;
}

interface TimeDayGroup {
  day: Date;
  items: ZeitEintrag[];
  hours: number;
}

/**
 * Detailseite eines Stammkunden-Auftrags: sichtbarer Status-Workflow (Stepper),
 * Auftragsdaten und die Einsätze nach Tagen gruppiert. Der Bauleiter plant hier
 * oder springt direkt ins Planungsboard.
 */
@Component({
  selector: 'app-auftrag-detail',
  templateUrl: './auftrag-detail.component.html',
  styleUrls: ['./auftrag-detail.component.css', '../stammkunden-theme.css']
})
export class AuftragDetailComponent implements OnInit, OnDestroy {
  customerId!: number;
  auftragId!: number;
  auftrag: Auftrag | null = null;
  assignments: AuftragAssignment[] = [];
  users: PlannableUser[] = [];
  workers: StammkundeWorker[] = [];
  myMemberships: MyStammkundeMembership[] = [];
  loading = true;
  statusSaving = false;

  /** Einsätze nach Tagen gruppiert (memoized – wird bei jeder Datenänderung neu aufgebaut). */
  dayGroups: DayGroup[] = [];

  /** Material-Übersicht: Gesamtsummen (gleiches Material + Einheit zusammengezählt). */
  materialTotals: EinsatzMaterial[] = [];
  /** Material-Übersicht: nur Tage/Einsätze, die Material haben. */
  materialDayGroups: DayGroup[] = [];
  materialView: 'gesamt' | 'einsatz' = 'gesamt';

  /** Umschaltung der Stunden – wie beim Material. Nur für Takeda sichtbar. */
  hoursView: 'gesamt' | 'einsatz' = 'gesamt';

  /**
   * Aggregierte Takeda-Daten (Stunden + Material aus den Tagesberichten).
   * null bei Nicht-Takeda-Aufträgen: dort bleibt alles wie bisher.
   */
  takedaProjekt: TakedaProjektDaten | null = null;
  takedaProjektLoading = false;
  timeEinsatzGroups: TimeEinsatzGroup[] = [];

  /**
   * Bepreisung aus dem geschützten Endpunkt. Bleibt null, wenn der Benutzer keinen
   * Zugriff hat (Vorarbeiter) – dann zeigt die Ansicht wie bisher keine Preise.
   */
  takedaPricing: TakedaPricing | null = null;
  /** Freies Material, dessen Preis gerade gespeichert wird. */
  savingFreiesMaterialId: number | null = null;

  /** Stunden-Erfassung (ohne Preise) – nach Tagen gruppiert. */
  timeEntries: ZeitEintrag[] = [];
  timeDayGroups: TimeDayGroup[] = [];
  totalHours = 0;

  readonly statusLabels = AUFTRAG_STATUS_LABELS;
  readonly statusSteps = AUFTRAG_STATUS_ORDER;
  readonly timeStatusLabels = TIME_ENTRY_STATUS_LABELS;
  readonly materialStatusLabels = MATERIAL_STATUS_LABELS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private stammkundeService: StammkundeService,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  private readonly subs = new Subscription();

  ngOnInit(): void {
    this.subs.add(this.route.paramMap.subscribe(params => {
      this.customerId = Number(params.get('id'));
      this.auftragId = Number(params.get('auftragId'));
      this.load();
    }));

    // Einsatzansicht über ?einsatz=<id>: verlinkbar, „Zurück“ im Browser schließt sie.
    this.subs.add(this.route.queryParamMap.subscribe(params => {
      const id = Number(params.get('einsatz'));
      this.setzeFokus(Number.isFinite(id) && id > 0 ? id : null);
    }));

    // Live-Suche für das nachträgliche Hinzufügen von LV-Positionen:
    // 300 ms Debounce, ab 2 Zeichen, laufende Anfragen werden verworfen.
    this.subs.add(this.lvAddSearch$
      .pipe(
        debounceTime(300),
        map(value => (value ?? '').trim()),
        distinctUntilChanged(),
        switchMap(term => {
          if (term.length < 2) {
            this.lvAddLoading = false;
            return of<TakedaTagesberichtLvPosition[]>([]);
          }
          this.lvAddLoading = true;
          return this.stammkundeService
            .searchTakedaProjektLvPositionen(this.auftragId, term)
            .pipe(catchError(err => {
              this.snackBar.open(
                err?.error?.message || 'Die LV-Positionen konnten nicht geladen werden.',
                'OK', { duration: 5000 });
              return of<TakedaTagesberichtLvPosition[]>([]);
            }));
        })
      )
      .subscribe(results => {
        this.lvAddResults = results ?? [];
        this.lvAddLoading = false;
      }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.lvAddSearch$.complete();
  }

  /** Planen darf der Bauleiter DIESES Stammkunden oder Admin/SuperAdmin. */
  get canManage(): boolean {
    if (this.authService.isSuperAdmin() || this.authService.isAdmin()) return true;
    return this.myMemberships.some(m => m.customerId === this.customerId && m.role === 'Bauleiter');
  }

  load(): void {
    this.loading = true;
    forkJoin({
      auftrag: this.stammkundeService.getAuftrag(this.auftragId),
      assignments: this.stammkundeService.getAssignments(this.auftragId),
      members: this.stammkundeService.getMembers(this.customerId),
      workers: this.stammkundeService.getWorkers(this.customerId),
      permissions: this.authService.getMyPermissions()
    }).subscribe({
      next: result => {
        this.auftrag = result.auftrag;
        this.assignments = result.assignments ?? [];
        this.rebuildDayGroups();
        // Verlinkter Einsatz existiert nicht (mehr) – zurück zur Auftragsansicht.
        if (this.fokusEinsatzId != null && !this.fokusEinsatz) {
          this.einsatzSchliessen();
        }
        // Planbar ist nur das Team dieses Stammkunden (keine globale Benutzerliste).
        this.users = (result.members ?? []).map(m => ({ id: m.userId, userName: m.userName, email: m.email }));
        this.workers = result.workers ?? [];
        this.myMemberships = result.permissions?.stammkundenMemberships ?? [];
        this.loading = false;
        // Stunden separat laden: fehlt die Tabelle (Migration noch nicht eingespielt),
        // bleibt der Auftrag trotzdem bedienbar.
        this.loadTimeEntries();
        // Takeda-Aggregation nur für Takeda-Aufträge.
        this.loadTakedaProjektDaten();
        this.loadTakedaPricing();
      },
      error: () => {
        this.snackBar.open('Fehler beim Laden des Auftrags', 'OK', { duration: 4000 });
        this.loading = false;
      }
    });
  }

  // ── Status-Workflow ──

  statusIndex(status: AuftragStatus): number {
    return this.statusSteps.indexOf(status);
  }

  get currentStatusIndex(): number {
    return this.auftrag ? this.statusIndex(this.auftrag.status) : 0;
  }

  /**
   * Klick auf einen Workflow-Schritt setzt den Status. Wer wohin darf, steht in
   * canSetStatus; bei Takeda kommt die Bepreisungsprüfung dazu – die entscheidet
   * allein der Server, deshalb bleibt der Status bei einem Fehler stehen.
   */
  setStatus(status: AuftragStatus): void {
    if (!this.auftrag || this.auftrag.status === status || this.statusSaving) return;
    if (!this.canSetStatus(status)) return;

    const payload = {
      customerId: this.auftrag.customerId,
      title: this.auftrag.title,
      description: this.auftrag.description,
      orderNumber: this.auftrag.orderNumber,
      receivedDate: this.auftrag.receivedDate,
      startDate: this.auftrag.startDate,
      dueDate: this.auftrag.dueDate,
      location: this.auftrag.location,
      primaryWarehouseId: this.auftrag.primaryWarehouseId,
      status
    };

    this.statusSaving = true;
    this.stammkundeService.updateAuftrag(this.auftrag.id, payload).subscribe({
      next: updated => {
        this.statusSaving = false;
        this.auftrag = updated;
        // Preis-Sichtbarkeit und Stunden-Snapshots ändern sich mit dem Status.
        this.loadTakedaPricing();
        this.loadTimeEntries();
        this.snackBar.open(`Status: ${this.statusLabels[status]}`, 'OK', { duration: 3000 });
      },
      error: err => {
        this.statusSaving = false;
        // Der alte Status bleibt bewusst stehen – die Ansicht darf nicht
        // weiterschalten, wenn der Server die Bepreisung abgelehnt hat.
        const message = err?.error?.message || 'Status konnte nicht gesetzt werden.';
        this.snackBar.open(message, 'OK', { duration: 8000 });
      }
    });
  }

  // ─────────────────────────────────────────────
  // Einsatzansicht
  //
  // Ein Einsatz komplett auf einen Blick – Stunden und Material – mit denselben
  // Bearbeitungsfunktionen wie die Auftragsansicht. Es ist bewusst dieselbe Seite,
  // nur auf den Einsatz gefiltert: jede Änderung landet in denselben Datensätzen
  // und ist ohne Umweg auch in den Listen des Auftrags sichtbar.
  // ─────────────────────────────────────────────

  fokusEinsatzId: number | null = null;

  get fokusEinsatz(): AuftragAssignment | undefined {
    return this.fokusEinsatzId == null
      ? undefined
      : this.assignments.find(a => a.id === this.fokusEinsatzId);
  }

  einsatzOeffnen(e: AuftragAssignment): void {
    if (!this.canManage) { return; }
    this.router.navigate([], { relativeTo: this.route, queryParams: { einsatz: e.id } });
  }

  einsatzSchliessen(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { einsatz: null }, queryParamsHandling: 'merge' });
  }

  private setzeFokus(id: number | null): void {
    if (id === this.fokusEinsatzId) { return; }
    this.fokusEinsatzId = id;

    // Ein Einsatz zeigt sich immer aufgeschlüsselt – „Gesamt" hätte hier keinen Sinn.
    if (id != null) {
      this.materialView = 'einsatz';
      this.hoursView = 'einsatz';
      window.scrollTo({ top: 0 });
    }

    // Offene Suchen gehören zur vorherigen Ansicht.
    this.lvAddBereichId = null;
    this.lvAddAssignmentId = null;
    this.zuordnung = null;
    this.leererFokusBlock = null;

    this.rebuildDayGroups();
    this.rebuildTimeGroups();
  }

  /** Nur Einträge des geöffneten Einsatzes (außerhalb der Einsatzansicht alle). */
  nurFokus<T extends { assignmentId?: number | null }>(liste: T[] | null | undefined): T[] {
    const alle = liste ?? [];
    return this.fokusEinsatzId == null ? alle : alle.filter(x => x.assignmentId === this.fokusEinsatzId);
  }

  /**
   * Takeda-Material "Nach Einsatz". Hat der geöffnete Einsatz noch gar kein
   * Material, steht ein leerer Block bereit, in den der Bauleiter nachträgt –
   * der Server legt den Container dafür über die einsatzbezogenen Endpunkte an.
   */
  get takedaMaterialEinsaetze(): TakedaProjektEinsatzMaterial[] {
    const eigene = this.nurFokus(this.takedaProjekt?.materialNachEinsatz);
    const fokus = this.fokusEinsatz;

    if (!fokus || eigene.length > 0 || !this.canEditTakedaMaterial) {
      return eigene;
    }

    // Einmal gebaut und gemerkt: ein neues Objekt je Prüfung würde die Zeile neu zeichnen.
    if (this.leererFokusBlock?.assignmentId !== fokus.id) {
      this.leererFokusBlock = {
        assignmentId: fokus.id,
        assignmentTitle: fokus.title,
        workDate: fokus.start,
        arbeitsbereiche: [{
          arbeitsbereichId: 0,
          assignmentId: fokus.id,
          referenzTyp: 'Ses',
          referenzNummer: '',
          lvPositionen: [],
          freieMaterialien: []
        }]
      };
    }

    return [this.leererFokusBlock];
  }

  private leererFokusBlock: TakedaProjektEinsatzMaterial | null = null;

  /** Hat der geöffnete Einsatz sichtbares Takeda-Material? */
  get fokusHatTakedaMaterial(): boolean {
    return this.takedaMaterialEinsaetze.some(e => e.arbeitsbereiche.some(
      b => b.lvPositionen.length > 0 || b.freieMaterialien.length > 0));
  }

  /** Material eines Nicht-Takeda-Einsatzes über den bestehenden Erfassungsdialog ändern. */
  einsatzMaterialBearbeiten(e: AuftragAssignment): void {
    if (!this.canManage || !this.auftrag) { return; }

    this.dialog.open(MaterialErfassungComponent, {
      width: '620px',
      maxWidth: '96vw',
      data: {
        auftrag: this.auftrag,
        assignmentId: e.id,
        einsatzLabel: e.title || this.auftrag.title,
        materials: e.materials ?? [],
        useQueue: false
      }
    }).afterClosed().subscribe(result => {
      if (result?.ok) {
        this.reloadAssignments();
        this.snackBar.open('Material gespeichert', 'OK', { duration: 3000 });
      }
    });
  }

  private datumFuerEingabe(iso: string): string {
    const d = new Date(iso);
    const zwei = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;
  }

  // ── Einsätze (nach Tagen gruppiert) ──

  private rebuildDayGroups(): void {
    const groups = new Map<string, DayGroup>();
    const sorted = [...this.assignments].sort((a, b) => a.start.localeCompare(b.start));
    for (const e of sorted) {
      const d = new Date(e.start);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!groups.has(key)) {
        groups.set(key, { day: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [] });
      }
      groups.get(key)!.items.push(e);
    }
    this.dayGroups = Array.from(groups.values());

    // Material-Übersicht: Gesamtsummen (Name + Einheit als Schlüssel) ...
    // In der Einsatzansicht nur das Material dieses Einsatzes.
    const materialQuelle = this.fokusEinsatzId == null
      ? this.assignments
      : this.assignments.filter(e => e.id === this.fokusEinsatzId);
    const totals = new Map<string, EinsatzMaterial>();
    for (const e of materialQuelle) {
      for (const m of e.materials ?? []) {
        const key = `${m.name.toLowerCase()}|${m.unit}`;
        const existing = totals.get(key);
        if (existing) {
          existing.quantity += m.quantity;
        } else {
          totals.set(key, { name: m.name, quantity: m.quantity, unit: m.unit });
        }
      }
    }
    this.materialTotals = Array.from(totals.values())
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));

    // ... und je Einsatz-Termin (nur Einsätze mit Material).
    this.materialDayGroups = this.dayGroups
      .map(g => ({
        day: g.day,
        items: g.items.filter(e => (e.materials?.length ?? 0) > 0 && materialQuelle.includes(e))
      }))
      .filter(g => g.items.length > 0);
  }

  // ── Stunden-Erfassung ──

  /** Angezeigte Zeiteinträge: alle, in der Einsatzansicht nur die des Einsatzes. */
  private get sichtbareZeiteintraege(): ZeitEintrag[] {
    return this.fokusEinsatzId == null
      ? this.timeEntries
      : this.timeEntries.filter(t => t.assignmentId === this.fokusEinsatzId);
  }

  private rebuildTimeGroups(): void {
    const groups = new Map<string, TimeDayGroup>();
    const sorted = [...this.sichtbareZeiteintraege]
      .sort((a, b) => a.workDate.localeCompare(b.workDate) || a.personName.localeCompare(b.personName, 'de'));
    let total = 0;
    for (const t of sorted) {
      total += t.hours;
      const d = new Date(t.workDate);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!groups.has(key)) {
        groups.set(key, { day: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [], hours: 0 });
      }
      const g = groups.get(key)!;
      g.items.push(t);
      g.hours += t.hours;
    }
    this.timeDayGroups = Array.from(groups.values());
    this.totalHours = total;

    // Zusätzliche Gruppierung für die Takeda-Ansicht "Nach Einsatz".
    this.rebuildTimeEinsatzGroups();
  }

  get hasTimeEntries(): boolean {
    return this.timeDayGroups.length > 0;
  }

  // ─────────────────────────────────────────────
  // Takeda-Projektansicht
  //
  // Die Aggregation liefert der Server (GET {id}/takeda/project-data) – das
  // Frontend gruppiert nur noch die bereits geladenen Zeiteinträge, damit deren
  // Status-Chips und Aktionen unverändert nutzbar bleiben.
  // ─────────────────────────────────────────────

  /** Serverflag am Auftrag, keine Namensprüfung. */
  get isTakedaAuftrag(): boolean {
    return this.auftrag?.isTakeda === true;
  }

  get hasTakedaMaterial(): boolean {
    const p = this.takedaProjekt;
    if (!p) { return false; }
    return p.lvMaterialGesamt.length > 0 || p.freiesMaterialGesamt.length > 0;
  }

  private loadTakedaProjektDaten(): void {
    if (!this.isTakedaAuftrag) {
      this.takedaProjekt = null;
      return;
    }

    this.takedaProjektLoading = true;

    this.stammkundeService.getTakedaProjektDaten(this.auftragId).subscribe({
      next: daten => {
        this.takedaProjekt = daten;
        this.takedaProjektLoading = false;
      },
      error: err => {
        this.takedaProjekt = null;
        this.takedaProjektLoading = false;
        this.snackBar.open(
          err?.error?.message || 'Die Takeda-Projektdaten konnten nicht geladen werden.',
          'OK', { duration: 5000 });
      }
    });
  }

  /**
   * Gruppiert die vorhandenen Zeiteinträge nach Einsatz und Tag. Bewusst aus
   * `timeEntries` und nicht aus der Server-Aggregation: nur so behalten die Zeilen
   * ihren Freigabe-Status und die bestehenden Aktionen.
   */
  private rebuildTimeEinsatzGroups(): void {
    const groups = new Map<string, TimeEinsatzGroup>();

    const sorted = [...this.sichtbareZeiteintraege].sort((a, b) =>
      a.workDate.localeCompare(b.workDate)
      || (a.assignmentId ?? 0) - (b.assignmentId ?? 0)
      || a.personName.localeCompare(b.personName, 'de'));

    for (const t of sorted) {
      const d = new Date(t.workDate);
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const assignmentId = t.assignmentId ?? null;
      const key = `${assignmentId ?? 'ohne'}|${day.getTime()}`;

      if (!groups.has(key)) {
        const assignment = assignmentId != null
          ? this.assignments.find(a => a.id === assignmentId)
          : undefined;

        groups.set(key, {
          key,
          assignmentId,
          title: assignment?.title || assignment?.location || null,
          day,
          items: [],
          hours: 0
        });
      }

      const g = groups.get(key)!;
      g.items.push(t);
      g.hours += t.hours;
    }

    this.timeEinsatzGroups = Array.from(groups.values());
  }

  trackByTimeEinsatzGroup(_: number, g: TimeEinsatzGroup): string {
    return g.key;
  }

  trackByTakedaLvMaterial(_: number, m: { lvPositionId?: number | null; nummer: string }): string {
    return m.lvPositionId != null ? `id:${m.lvPositionId}` : `nr:${m.nummer}`;
  }

  /**
   * Eindeutig über beide Herkünfte: Takeda-Zeilen tragen ihre Id, noch nicht
   * übernommener Altbestand hat id 0 und wird über die Legacy-Id unterschieden.
   * Ein reines m.id wäre für alle Altzeilen 0 – Angular bekäme doppelte Schlüssel.
   */
  trackByTakedaFreiesMaterial(
    _: number,
    m: { id: number; legacyMaterialId?: number | null }
  ): string {
    return m.id > 0 ? `t:${m.id}` : `l:${m.legacyMaterialId ?? 0}`;
  }

  /** Haupttext einer LV-Zeile: Bezeichnung bevorzugt, sonst Stichwort. */
  takedaLvText(m: { bezeichnung?: string | null; stichwort?: string | null }): string {
    return (m.bezeichnung?.trim() || m.stichwort?.trim() || '').trim();
  }

  /** Zusatzzeile: nur, wenn das Stichwort nicht schon der Haupttext ist. */
  takedaLvZusatz(m: { bezeichnung?: string | null; stichwort?: string | null }): string | null {
    const stichwort = m.stichwort?.trim();
    if (!stichwort) { return null; }
    return m.bezeichnung?.trim() ? stichwort : null;
  }

  takedaReferenzLabel(referenzTyp?: string | null): string {
    return referenzTyp === 'Angebot' ? 'Angebot' : 'SES';
  }

  // ─────────────────────────────────────────────
  // Materialkorrektur durch den Bauleiter
  //
  // Korrigiert werden die echten Takeda-Datensätze, damit Projektansicht,
  // Bepreisung und spätere Exporte dieselben Werte sehen. Der Tagesbericht
  // bleibt für den Vorarbeiter nach dem Abschließen unverändert gesperrt.
  // ─────────────────────────────────────────────

  /**
   * Korrigieren darf der Bauleiter dieses Stammkunden (sowie Admin/SuperAdmin)
   * bis einschließlich "Abgeschlossen". Offene Tagesberichte (Entwurf) bleiben
   * beim Vorarbeiter. Der Server entscheidet.
   */
  get canEditTakedaMaterial(): boolean {
    if (!this.isTakedaAuftrag || !this.canManage) { return false; }
    return this.vorBepreist;
  }

  /** Bis einschließlich "Abgeschlossen" – danach sind die Werte Abrechnungsgrundlage. */
  get vorBepreist(): boolean {
    const status = this.auftrag?.status;
    return !!status && this.statusIndex(status) < this.statusIndex('Priced');
  }

  /** Material eines Einsatztages korrigierbar: Bearbeitungsmodus an und Tagesbericht abgeschlossen. */
  einsatzMaterialBearbeitbar(e: TakedaProjektEinsatzMaterial): boolean {
    return this.materialBearbeitung && !e.tagesberichtOffen;
  }

  /**
   * Bearbeitungsmodus für Material. Bewusst ein expliziter Schalter: beide
   * Ansichten bleiben zunächst reine Übersicht, damit man beim Durchsehen nicht
   * versehentlich Mengen verstellt.
   */
  materialEditMode = false;

  /** Tatsächlich bearbeitbar: Recht + erlaubter Status + eingeschalteter Modus. */
  get materialBearbeitung(): boolean {
    return this.canEditTakedaMaterial && this.materialEditMode;
  }

  toggleMaterialEdit(): void {
    // Hier zählt nur das Recht – der Modus wird ja gerade erst eingeschaltet.
    if (!this.canEditTakedaMaterial) { return; }

    this.materialEditMode = !this.materialEditMode;

    // Beim Verlassen eine offene LV-Suche schließen.
    if (!this.materialEditMode) {
      this.lvAddBereichId = null;
      this.zuordnung = null;
      this.lvAddSearch = '';
      this.lvAddResults = [];
    }
  }

  /** Im Bearbeitungsmodus darauf hinweisen, wo neues Material angelegt wird. */
  get materialKorrekturHinweis(): boolean {
    return this.materialBearbeitung && this.materialView === 'gesamt';
  }

  /**
   * Leere Arbeitsbereiche nur zeigen, wenn korrigiert werden darf – dort trägt der
   * Bauleiter vergessenes Material nach. Sonst wären sie nur Rauschen.
   */
  zeigeArbeitsbereich(b: { lvPositionen: unknown[]; freieMaterialien: unknown[] }): boolean {
    return this.materialBearbeitung
      || b.lvPositionen.length > 0
      || b.freieMaterialien.length > 0;
  }

  zeigeEinsatzMaterial(e: { arbeitsbereiche: { lvPositionen: unknown[]; freieMaterialien: unknown[] }[] }): boolean {
    return e.arbeitsbereiche.some(b => this.zeigeArbeitsbereich(b));
  }

  async setLvMaterialMenge(zeile: TakedaProjektLvMaterial, wert: string): Promise<void> {
    if (!this.materialBearbeitung || !zeile.id) { return; }

    const menge = Number(wert);
    if (!Number.isFinite(menge) || menge <= 0) {
      this.snackBar.open('Die Menge muss größer als 0 sein.', 'OK', { duration: 4000 });
      this.loadTakedaProjektDaten();
      return;
    }
    if (menge === zeile.menge) { return; }

    this.stammkundeService.setTakedaLvPositionMenge(this.auftragId, zeile.id, menge).subscribe({
      next: () => this.reloadTakedaMaterial('Menge geändert'),
      error: err => this.materialFehler(err, 'Die Menge konnte nicht geändert werden.')
    });
  }

  removeLvMaterial(zeile: TakedaProjektLvMaterial): void {
    if (!this.materialBearbeitung || !zeile.id) { return; }

    const label = [zeile.nummer, this.takedaLvText(zeile)].filter(Boolean).join(' – ');

    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { title: 'LV-Position entfernen', message: `„${label}“ wirklich entfernen?` }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.stammkundeService.deleteTakedaProjektLvPosition(this.auftragId, zeile.id).subscribe({
        next: () => this.reloadTakedaMaterial('LV-Position entfernt'),
        error: err => this.materialFehler(err, 'Die LV-Position konnte nicht entfernt werden.')
      });
    });
  }

  // ── LV-Position hinzufügen (Suche je Arbeitsbereich) ──

  /**
   * Arbeitsbereich, für den gerade die LV-Suche offen ist.
   * 0 = Einsatzblock, der noch keinen Takeda-Arbeitsbereich hat; dann
   * unterscheidet zusätzlich lvAddAssignmentId die Blöcke.
   */
  lvAddBereichId: number | null = null;
  lvAddAssignmentId: number | null = null;
  lvAddSearch = '';
  lvAddResults: TakedaTagesberichtLvPosition[] = [];
  lvAddLoading = false;
  private readonly lvAddSearch$ = new Subject<string>();

  /** Ist die LV-Suche für genau diesen Block offen? */
  lvAddOffen(arbeitsbereichId: number, assignmentId?: number | null): boolean {
    if (this.lvAddBereichId !== arbeitsbereichId) { return false; }
    // Ohne Arbeitsbereich ist die Id 0 und damit nicht eindeutig.
    if (arbeitsbereichId !== 0) { return true; }
    return this.lvAddAssignmentId === (assignmentId ?? null);
  }

  toggleLvAdd(arbeitsbereichId: number, assignmentId?: number | null): void {
    if (!this.materialBearbeitung) { return; }

    const warOffen = this.lvAddOffen(arbeitsbereichId, assignmentId);

    // Nur eine Suche gleichzeitig – eine offene LV-Zuordnung schließt sich.
    this.zuordnung = null;
    this.lvAddBereichId = warOffen ? null : arbeitsbereichId;
    this.lvAddAssignmentId = warOffen ? null : (assignmentId ?? null);
    this.lvAddSearch = '';
    this.lvAddResults = [];
    this.lvAddLoading = false;
  }

  onLvAddSearch(value: string): void {
    this.lvAddSearch = value;
    this.lvAddSearch$.next(value);
  }

  addLvMaterial(position: TakedaTagesberichtLvPosition): void {
    if (!this.materialBearbeitung || this.lvAddBereichId == null) { return; }

    // Hat der Einsatz noch keinen Arbeitsbereich, legt ihn der Server über den
    // einsatzbezogenen Endpunkt an.
    const request = this.lvAddBereichId === 0
      ? (this.lvAddAssignmentId == null
          ? null
          : this.stammkundeService.addTakedaEinsatzLvPosition(
              this.auftragId, this.lvAddAssignmentId, position.id, 1))
      : this.stammkundeService.addTakedaProjektLvPosition(
          this.auftragId, this.lvAddBereichId, position.id, 1);

    if (!request) {
      this.snackBar.open(
        'Diesem Materialblock fehlt der Einsatzbezug – die Position lässt sich hier nicht hinzufügen.',
        'OK', { duration: 6000 });
      return;
    }

    request.subscribe({
      next: () => {
        this.lvAddBereichId = null;
        this.lvAddAssignmentId = null;
        this.lvAddSearch = '';
        this.lvAddResults = [];
        this.reloadTakedaMaterial('LV-Position hinzugefügt');
      },
      error: err => this.materialFehler(err, 'Die LV-Position konnte nicht hinzugefügt werden.')
    });
  }

  // ── Freies Material ──

  async setFreiesMaterialFeld(
    zeile: TakedaProjektFreiesMaterial,
    feld: 'bezeichnung' | 'menge' | 'einheit',
    wert: string
  ): Promise<void> {
    if (!this.materialBearbeitung) { return; }

    const bezeichnung = (feld === 'bezeichnung' ? wert : zeile.bezeichnung)?.trim() ?? '';
    const menge = feld === 'menge' ? Number(wert) : zeile.menge;
    const einheit = feld === 'einheit' ? wert?.trim() : zeile.einheit;

    if (!bezeichnung) {
      this.snackBar.open('Bitte eine Bezeichnung angeben.', 'OK', { duration: 4000 });
      this.loadTakedaProjektDaten();
      return;
    }
    if (!Number.isFinite(menge) || menge <= 0) {
      this.snackBar.open('Die Menge muss größer als 0 sein.', 'OK', { duration: 4000 });
      this.loadTakedaProjektDaten();
      return;
    }

    // Nichts geändert (z. B. nur in das Feld geklickt, dann auf den Papierkorb):
    // kein Request. Sonst entstünde bei einer Alterfassung schon beim Verlassen
    // des Feldes eine Korrekturzeile.
    if (bezeichnung === (zeile.bezeichnung?.trim() ?? '')
        && menge === zeile.menge
        && (einheit || '') === (zeile.einheit?.trim() || '')) {
      return;
    }

    const body = { bezeichnung, menge, einheit: einheit || null };

    /*
     * Altmaterial aus der Zeit vor der Takeda-Erweiterung hat noch keine
     * Takeda-Zeile. Der erste Aufruf übernimmt es serverseitig in die
     * Takeda-Struktur; die ursprüngliche Zeile bleibt dabei erhalten. Für den
     * Benutzer ist das nicht unterscheidbar – es ist dieselbe Materialliste.
     */
    const request = this.istLegacy(zeile)
      ? this.stammkundeService.updateTakedaLegacyMaterial(
          this.auftragId, zeile.legacyMaterialId!, body)
      : this.stammkundeService.updateTakedaFreiesMaterial(this.auftragId, zeile.id, body);

    request.subscribe({
      // Ein bereits gepflegter Preis bleibt serverseitig unberührt.
      next: () => this.reloadTakedaMaterial('Material geändert'),
      error: err => this.materialFehler(err, 'Das Material konnte nicht geändert werden.')
    });
  }

  /** Noch nicht in die Takeda-Struktur übernommener Altbestand. */
  istLegacy(zeile: TakedaProjektFreiesMaterial): boolean {
    return zeile.art === 'Legacy' && zeile.legacyMaterialId != null;
  }

  /**
   * Freies Material oder Alterfassung dauerhaft entfernen – unabhängig von der
   * Menge. Beide Arten haben ihren eigenen Endpunkt, weil sie in verschiedenen
   * Tabellen liegen; der Server nimmt eine Alterfassung samt Korrektur mit,
   * damit sie nach dem Neuladen nicht wieder auftaucht.
   */
  removeFreiesMaterial(zeile: TakedaProjektFreiesMaterial): void {
    if (!this.materialBearbeitung) { return; }

    const legacy = this.istLegacy(zeile);
    if (!legacy && !(zeile.id > 0)) { return; }

    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Material entfernen',
        message: legacy
          ? `„${zeile.bezeichnung}“ stammt aus der ursprünglichen Erfassung. Wirklich dauerhaft entfernen?`
          : `„${zeile.bezeichnung}“ wirklich entfernen?`
      }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }

      const request = legacy
        ? this.stammkundeService.deleteTakedaLegacyMaterial(this.auftragId, zeile.legacyMaterialId!)
        : this.stammkundeService.deleteTakedaFreiesMaterial(this.auftragId, zeile.id);

      request.subscribe({
        next: () => this.reloadTakedaMaterial('Material entfernt'),
        error: err => this.materialFehler(err, 'Das Material konnte nicht entfernt werden.')
      });
    });
  }

  // ── LV-Zuordnung nachträglich ändern ──
  //
  // Nutzt dieselbe LV-Suche wie „+ LV-Position“ (lvAddSearch/lvAddResults); es ist
  // immer nur eine der beiden Suchen offen. Die erfasste Menge bleibt erhalten.

  /** Zeile, deren LV-Zuordnung gerade geändert wird. */
  zuordnung: { art: 'Lv' | 'Frei' | 'Legacy'; zeileId: number; key: string } | null = null;
  zuordnungLaeuft = false;

  private zuordnungsQuelle(
    art: 'Lv' | 'Frei',
    zeile: TakedaProjektLvMaterial | TakedaProjektFreiesMaterial
  ): { art: 'Lv' | 'Frei' | 'Legacy'; zeileId: number; key: string } | null {
    if (art === 'Lv') {
      return zeile.id > 0 ? { art: 'Lv', zeileId: zeile.id, key: `lv:${zeile.id}` } : null;
    }
    const frei = zeile as TakedaProjektFreiesMaterial;
    if (this.istLegacy(frei)) {
      return { art: 'Legacy', zeileId: frei.legacyMaterialId!, key: `legacy:${frei.legacyMaterialId}` };
    }
    return frei.id > 0 ? { art: 'Frei', zeileId: frei.id, key: `frei:${frei.id}` } : null;
  }

  kannZuordnen(art: 'Lv' | 'Frei', zeile: TakedaProjektLvMaterial | TakedaProjektFreiesMaterial): boolean {
    return this.materialBearbeitung && this.zuordnungsQuelle(art, zeile) != null;
  }

  zuordnungOffen(art: 'Lv' | 'Frei', zeile: TakedaProjektLvMaterial | TakedaProjektFreiesMaterial): boolean {
    const quelle = this.zuordnungsQuelle(art, zeile);
    return !!quelle && this.zuordnung?.key === quelle.key;
  }

  toggleZuordnung(art: 'Lv' | 'Frei', zeile: TakedaProjektLvMaterial | TakedaProjektFreiesMaterial): void {
    if (!this.materialBearbeitung) { return; }
    const quelle = this.zuordnungsQuelle(art, zeile);
    if (!quelle) { return; }

    this.zuordnung = this.zuordnung?.key === quelle.key ? null : quelle;
    // Nur eine Suche gleichzeitig.
    this.lvAddBereichId = null;
    this.lvAddAssignmentId = null;
    this.lvAddSearch = '';
    this.lvAddResults = [];
    this.lvAddLoading = false;
  }

  /** `lvPositionId: null` = Zuordnung entfernen, als freies Material führen. */
  zuordnen(lvPositionId: number | null, bestaetigt = false): void {
    const quelle = this.zuordnung;
    if (!this.materialBearbeitung || !quelle || this.zuordnungLaeuft) { return; }

    this.zuordnungLaeuft = true;
    this.stammkundeService.setTakedaLvZuordnung(this.auftragId, {
      art: quelle.art, zeileId: quelle.zeileId, lvPositionId, bestaetigt
    }).subscribe({
      next: () => {
        this.zuordnungLaeuft = false;
        this.zuordnung = null;
        this.lvAddSearch = '';
        this.lvAddResults = [];
        this.reloadTakedaMaterial(lvPositionId == null
          ? 'LV-Zuordnung entfernt – als freies Material geführt'
          : 'LV-Zuordnung gespeichert');
      },
      error: err => {
        this.zuordnungLaeuft = false;

        // Preis oder Einheit ändern sich: erst nach ausdrücklicher Bestätigung.
        if (err?.status === 409 && err?.error?.bestaetigungErforderlich && !bestaetigt) {
          this.dialog.open(ConfirmDialogComponent, {
            width: '460px',
            data: { title: 'LV-Zuordnung ändern', message: err.error.message }
          }).afterClosed().subscribe(confirmed => {
            if (confirmed) { this.zuordnen(lvPositionId, true); }
          });
          return;
        }

        this.materialFehler(err, 'Die LV-Zuordnung konnte nicht geändert werden.');
      }
    });
  }

  addFreiesMaterial(arbeitsbereichId: number, assignmentId?: number | null): void {
    if (!this.materialBearbeitung) { return; }

    const body = { bezeichnung: 'Neues Material', menge: 1, einheit: null };

    // Hat der Einsatz noch keinen Arbeitsbereich, legt ihn der Server an.
    if (arbeitsbereichId === 0) {
      if (assignmentId == null) {
        this.snackBar.open(
          'Diesem Materialblock fehlt der Einsatzbezug – hier lässt sich kein Material anlegen.',
          'OK', { duration: 6000 });
        return;
      }

      this.stammkundeService.addTakedaEinsatzFreiesMaterial(
        this.auftragId, assignmentId, body).subscribe({
          next: () => this.reloadTakedaMaterial('Material hinzugefügt – bitte Bezeichnung anpassen'),
          error: err => this.materialFehler(err, 'Das Material konnte nicht hinzugefügt werden.')
        });
      return;
    }

    this.stammkundeService.addTakedaFreiesMaterial(this.auftragId, arbeitsbereichId, body).subscribe({
      next: () => this.reloadTakedaMaterial('Material hinzugefügt – bitte Bezeichnung anpassen'),
      error: err => this.materialFehler(err, 'Das Material konnte nicht hinzugefügt werden.')
    });
  }

  // ─────────────────────────────────────────────
  // Abrechnungsexport (PDF / Excel)
  //
  // Beide Ausgaben lesen dieselbe serverseitig gerechnete Abrechnung. Im Browser
  // wird bewusst nichts nachgerechnet – nur gesetzt bzw. heruntergeladen.
  // ─────────────────────────────────────────────

  exportLaeuft: 'pdf' | 'excel' | null = null;
  /** Excel-Exportdialog offen – Buttons gesperrt, der Download läuft im Dialog. */
  excelDialogOffen = false;
  nachtragenLaeuft = false;

  /**
   * Altauftrag: bepreist, bevor es die Stundensatz-Schnappschüsse gab. Dann sind
   * die Stundenbeträge unvollständig – der Bauleiter kann sie nachtragen.
   */
  get stundensaetzeFehlen(): boolean {
    return this.zeigeStundenPreise && (this.takedaPricing?.stundenOhneSnapshot ?? 0) > 0;
  }

  nachtragenStundensaetze(): void {
    if (!this.canManage || this.nachtragenLaeuft) { return; }
    this.nachtragenLaeuft = true;

    this.stammkundeService.nachtragenTakedaStundensaetze(this.auftragId).subscribe({
      next: res => {
        this.nachtragenLaeuft = false;
        this.loadTakedaPricing();
        this.snackBar.open(res?.message || 'Stundensätze nachgetragen.', 'OK', { duration: 5000 });
      },
      error: err => {
        this.nachtragenLaeuft = false;
        this.snackBar.open(
          this.exportFehler(err, 'Die Stundensätze konnten nicht nachgetragen werden.'),
          'OK', { duration: 9000 });
      }
    });
  }

  /**
   * Export erst, wenn der Auftrag abgerechnet ist – vorher wäre die Abrechnung
   * noch nicht endgültig. Serverseitig ist ab "Bepreist" erlaubt; die Oberfläche
   * ist hier bewusst strenger.
   */
  get canExportTakeda(): boolean {
    if (!this.isTakedaAuftrag || !this.canManage) { return false; }
    return this.auftrag?.status === 'Billed';
  }

  /** Macht aus einem HTTP-Fehler eine Meldung, mit der man etwas anfangen kann. */
  private exportFehler(err: any, standard: string): string {
    return takedaExportFehlerText(err, standard);
  }

  exportPdf(): void {
    if (!this.canExportTakeda || this.exportLaeuft) { return; }
    this.exportLaeuft = 'pdf';

    this.stammkundeService.getTakedaAbrechnung(this.auftragId).subscribe({
      next: async abrechnung => {
        try {
          const doc = await buildTakedaAbrechnungPdf(abrechnung);
          doc.save(takedaExportDateiname(abrechnung, 'pdf'));
        } catch {
          this.snackBar.open('Das PDF konnte nicht erzeugt werden.', 'OK', { duration: 5000 });
        } finally {
          this.exportLaeuft = null;
        }
      },
      error: err => {
        this.exportLaeuft = null;
        this.snackBar.open(
          this.exportFehler(err, 'Die Abrechnungsdaten konnten nicht geladen werden.'),
          'OK', { duration: 9000 });
      }
    });
  }

  /**
   * Excel-Export. Für Takeda öffnet sich ein Dialog mit drei Varianten
   * (Gesamtübersicht, ein bestimmter Einsatz, alle Einsätze einzeln); der
   * Download selbst läuft im Dialog. Solange er offen ist, bleibt der Button
   * gesperrt, damit keine zweite Anfrage gestartet wird.
   */
  exportExcel(): void {
    if (!this.canExportTakeda || this.exportLaeuft || this.excelDialogOffen) { return; }

    if (!this.isTakedaAuftrag) {
      this.exportExcelGesamt();
      return;
    }

    this.excelDialogOffen = true;

    const data: TakedaExcelExportDialogData = {
      auftragId: this.auftragId,
      auftragTitel: this.auftrag?.title ?? null
    };

    this.dialog.open(TakedaExcelExportDialogComponent, {
      width: '560px',
      maxWidth: '96vw',
      data
    }).afterClosed().subscribe(exportiert => {
      this.excelDialogOffen = false;
      if (exportiert) {
        this.snackBar.open('Excel-Datei heruntergeladen.', 'OK', { duration: 2500 });
      }
    });
  }

  /** Bisheriger Excel-Export (Gesamtübersicht) ohne Dialog. */
  private exportExcelGesamt(): void {
    this.exportLaeuft = 'excel';

    // Über HttpClient, damit der AuthInterceptor den Bearer-Token mitschickt –
    // ein einfacher Link oder window.open() käme ohne Authorization an.
    this.stammkundeService.downloadTakedaExcel(this.auftragId).subscribe({
      next: response => {
        this.exportLaeuft = null;

        if (!speichereTakedaExportDatei(response, 'Takeda-Abrechnung.xlsx')) {
          this.snackBar.open('Die Excel-Datei war leer.', 'OK', { duration: 5000 });
        }
      },
      error: err => {
        this.exportLaeuft = null;
        this.snackBar.open(
          this.exportFehler(err, 'Die Excel-Datei konnte nicht erzeugt werden.'),
          'OK', { duration: 9000 });
      }
    });
  }

  private reloadTakedaMaterial(meldung: string): void {
    // Projektansicht und Bepreisung hängen beide an den korrigierten Werten.
    this.loadTakedaProjektDaten();
    this.loadTakedaPricing();
    this.snackBar.open(meldung, 'OK', { duration: 2500 });
  }

  private materialFehler(err: any, standard: string): void {
    this.snackBar.open(err?.error?.message || standard, 'OK', { duration: 6000 });
    // Serverstand wiederherstellen, damit die Anzeige nicht lügt.
    this.loadTakedaProjektDaten();
  }

  // ─────────────────────────────────────────────
  // Takeda-Bepreisung
  //
  // Eigener, geschützter Endpunkt. Wer ihn nicht aufrufen darf (Vorarbeiter),
  // bekommt hier schlicht nichts – die Ansicht bleibt dann preisfrei.
  // ─────────────────────────────────────────────

  /** Preise gibt es nur, wenn der Server sie geliefert UND freigeschaltet hat. */
  get zeigeMaterialPreise(): boolean {
    return this.takedaPricing?.materialPreiseSichtbar === true;
  }

  get zeigeStundenPreise(): boolean {
    return this.takedaPricing?.stundenPreiseSichtbar === true;
  }

  get freiesMaterialBearbeitbar(): boolean {
    return this.takedaPricing?.freiesMaterialBearbeitbar === true;
  }

  private loadTakedaPricing(): void {
    // Nur für Takeda und nur für die, die den Stammkunden führen.
    if (!this.isTakedaAuftrag || !this.canManage) {
      this.takedaPricing = null;
      return;
    }

    this.stammkundeService.getTakedaPricing(this.auftragId).subscribe({
      next: pricing => this.takedaPricing = pricing,
      // Kein Snackbar: fehlender Zugriff ist hier ein normaler Zustand.
      error: () => this.takedaPricing = null
    });
  }

  /** Preis pro Einheit einer freien Materialposition speichern. */
  /** Läuft für genau diese Zeile gerade ein Speichervorgang? */
  istSpeichernd(zeile: TakedaPricingMaterialZeile): boolean {
    if (this.savingFreiesMaterialId == null) { return false; }
    return zeile.art === 'Legacy' && zeile.legacyMaterialId != null
      ? this.savingFreiesMaterialId === -zeile.legacyMaterialId
      : this.savingFreiesMaterialId === zeile.freiesMaterialId;
  }

  /**
   * Es fehlen noch Materialpreise – darunter Altmaterial, das erst beim
   * Bepreisen in die Takeda-Struktur übernommen wird. Solange das so ist,
   * verweigert der Server den Wechsel auf „Bepreist".
   */
  get materialPreiseFehlen(): boolean {
    return this.zeigeMaterialPreise && (this.takedaPricing?.materialOhnePreis ?? 0) > 0;
  }

  /** Bepreisbar sind freies Takeda-Material und Altbestand (letzterer wird dabei übernommen). */
  istBepreisbareZeile(zeile: TakedaPricingMaterialZeile): boolean {
    if (!this.freiesMaterialBearbeitbar) { return false; }
    return zeile.freiesMaterialId != null
      || (zeile.art === 'Legacy' && zeile.legacyMaterialId != null);
  }

  saveFreiesMaterialPreis(zeile: TakedaPricingMaterialZeile): void {
    if (!this.istBepreisbareZeile(zeile)) return;

    const preis = zeile.preisProEinheit;

    if (preis != null && (!Number.isFinite(Number(preis)) || Number(preis) < 0)) {
      this.snackBar.open('Der Preis darf nicht negativ sein.', 'OK', { duration: 4000 });
      return;
    }

    const istLegacy = zeile.art === 'Legacy' && zeile.legacyMaterialId != null;
    const wert = preis == null ? null : Number(preis);

    // Für die Sperre des Eingabefeldes: Legacy-Zeilen haben noch keine
    // Takeda-Id, deshalb als negativer Schlüssel.
    this.savingFreiesMaterialId = istLegacy
      ? -zeile.legacyMaterialId!
      : zeile.freiesMaterialId!;

    /*
     * Beim Altbestand ist das Bepreisen die erste Bearbeitung: der Server
     * übernimmt die Zeile mit ihren Ursprungswerten in die Takeda-Struktur und
     * speichert den Preis dort. Die ursprüngliche Zeile bleibt erhalten.
     */
    const request = istLegacy
      ? this.stammkundeService.setTakedaLegacyMaterialPreis(
          this.auftragId, zeile.legacyMaterialId!, wert)
      : this.stammkundeService.setTakedaFreiesMaterialPreis(
          this.auftragId, zeile.freiesMaterialId!, wert);

    request.subscribe({
      next: () => {
        this.savingFreiesMaterialId = null;
        // Summen und die Ansicht "Nach Einsatz" neu vom Server holen.
        this.loadTakedaPricing();
      },
      error: err => {
        this.savingFreiesMaterialId = null;
        this.snackBar.open(
          err?.error?.message || 'Der Preis konnte nicht gespeichert werden.',
          'OK', { duration: 5000 });
        this.loadTakedaPricing();
      }
    });
  }

  // ── Statusrechte ──

  /**
   * Weiteste Stelle, bis zu der ein reiner Vorarbeiter den Auftrag treiben darf –
   * spiegelt AuftragStatusPolicy.VorarbeiterMaxStatus im Backend.
   */
  private readonly vorarbeiterMaxIndex = AUFTRAG_STATUS_ORDER.indexOf('Recorded');

  /** Ist der Benutzer Vorarbeiter (ohne Bauleiter-Rolle) bei diesem Stammkunden? */
  private get istVorarbeiterHier(): boolean {
    if (this.canManage) return false;
    return this.myMemberships.some(
      m => m.customerId === this.customerId && m.role === 'Vorarbeiter');
  }

  /**
   * Ist die Statusleiste für den Benutzer überhaupt bedienbar? Steuert nur die
   * Optik (Cursor/Hinweis) – welcher einzelne Schritt erlaubt ist, entscheidet
   * canSetStatus, maßgeblich ist ohnehin der Server.
   */
  get canChangeStatus(): boolean {
    if (this.canManage) return true;
    return this.istVorarbeiterHier && this.currentStatusIndex < this.vorarbeiterMaxIndex;
  }

  /**
   * Darf der aktuelle Benutzer diesen Schritt setzen? Spiegelt die Serverregel:
   * Bauleiter/Admin/SuperAdmin setzen jeden Status frei – vorwärts wie rückwärts,
   * "Abgerechnet" eingeschlossen. Ein reiner Vorarbeiter nur bis einschließlich
   * "Erfasst" und nur, solange der Auftrag noch davor steht.
   *
   * Die Bepreisungsprüfung beim Wechsel auf "Bepreist"/"Abgerechnet" entscheidet
   * bewusst nur der Server; sie lässt sich hier nicht zuverlässig vorhersagen.
   */
  canSetStatus(ziel: AuftragStatus): boolean {
    const aktuell = this.auftrag?.status;
    if (!aktuell || this.statusSaving) return false;
    if (aktuell === ziel) return false;

    if (this.canManage) return true;

    if (!this.istVorarbeiterHier) return false;

    return this.currentStatusIndex < this.vorarbeiterMaxIndex
      && this.statusIndex(ziel) <= this.vorarbeiterMaxIndex;
  }

  timeStatusLabel(status: string): string {
    return (this.timeStatusLabels as Record<string, string>)[status] ?? status;
  }

  /** Personenauswahl für die Stundenerfassung: Team (mit Login) + Arbeiter (ohne Login). */
  private buildPeople(): ZeitPerson[] {
    const people: ZeitPerson[] = [];
    for (const u of this.users) {
      people.push({ label: u.userName || u.email || u.id, personUserId: u.id });
    }
    for (const w of this.workers) {
      people.push({ label: w.name, workerId: w.id });
    }
    return people;
  }

  openZeitForm(entry?: ZeitEintrag): void {
    if (!this.canManage || !this.auftrag) return;
    // In der Einsatzansicht gehören neue Stunden zu diesem Einsatz und seinem Tag.
    const fokus = entry ? undefined : this.fokusEinsatz;
    const dialogRef = this.dialog.open(ZeitEintragFormComponent, {
      width: '520px',
      maxWidth: '96vw',
      data: {
        auftrag: this.auftrag,
        assignmentId: entry?.assignmentId ?? fokus?.id ?? null,
        defaultDate: fokus ? this.datumFuerEingabe(fokus.start) : undefined,
        people: this.buildPeople(),
        entry,
        useQueue: false
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.ok) {
        this.reloadTimeEntries();
        this.snackBar.open(entry ? 'Stunden aktualisiert' : 'Stunden erfasst', 'OK', { duration: 3000 });
      }
    });
  }

  get submittedCount(): number {
    return this.timeEntries.filter(t => t.status === 'Submitted').length;
  }

  /** Alle noch nicht freigegebenen Stunden auf einmal freigeben (Bauleiter). */
  approveAll(): void {
    if (!this.canManage || this.submittedCount === 0) return;
    this.stammkundeService.approveAllTimeEntries(this.auftragId).subscribe({
      next: res => {
        this.snackBar.open(`${res.approved} Eintrag/Einträge freigegeben`, 'OK', { duration: 3000 });
        this.loadTimeEntries();
      },
      error: err => this.snackBar.open(
        err?.error?.message || 'Freigabe fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  approveEntry(entry: ZeitEintrag, event: Event): void {
    event.stopPropagation();
    if (!this.canManage) return;
    this.stammkundeService.approveTimeEntry(this.auftragId, entry.id).subscribe({
      next: updated => {
        this.applyEntry(updated);
        this.snackBar.open('Stunden freigegeben', 'OK', { duration: 3000 });
      },
      error: err => this.snackBar.open(
        err?.error?.message || 'Freigabe fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  reopenEntry(entry: ZeitEintrag, event: Event): void {
    event.stopPropagation();
    if (!this.canManage) return;
    this.stammkundeService.reopenTimeEntry(this.auftragId, entry.id).subscribe({
      next: updated => {
        this.applyEntry(updated);
        this.snackBar.open('Freigabe zurückgenommen', 'OK', { duration: 3000 });
      },
      error: err => this.snackBar.open(
        err?.error?.message || 'Aktion fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  deleteEntry(entry: ZeitEintrag, event: Event): void {
    event.stopPropagation();
    if (!this.canManage) return;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: { title: 'Stunden löschen', message: `Eintrag (${entry.hours} h · ${entry.personName}) wirklich löschen?` }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService.deleteTimeEntry(this.auftragId, entry.id).subscribe({
          next: () => {
            this.timeEntries = this.timeEntries.filter(t => t.id !== entry.id);
            this.rebuildTimeGroups();
            this.snackBar.open('Stunden gelöscht', 'OK', { duration: 3000 });
          },
          error: err => this.snackBar.open(
            err?.error?.message || 'Löschen fehlgeschlagen', 'OK', { duration: 4000 })
        });
      }
    });
  }

  private applyEntry(updated: ZeitEintrag): void {
    this.timeEntries = this.timeEntries.map(t => t.id === updated.id ? updated : t);
    this.rebuildTimeGroups();
  }

  private loadTimeEntries(): void {
    this.stammkundeService.getTimeEntries(this.auftragId).subscribe({
      next: entries => {
        this.timeEntries = entries ?? [];
        this.rebuildTimeGroups();
      },
      error: () => { /* z.B. Tabelle noch nicht migriert – Bereich bleibt leer */ }
    });
  }

  private reloadTimeEntries(): void {
    this.loadTimeEntries();
    // Die Stunden-Summen der Takeda-Ansicht kommen vom Server und müssen mit.
    this.loadTakedaProjektDaten();
    // Status kann durch die erste Stundenmeldung auf "In Arbeit" springen.
    this.stammkundeService.getAuftrag(this.auftragId).subscribe({
      next: auftrag => this.auftrag = auftrag,
      error: () => { /* Anzeige bleibt unverändert */ }
    });
  }

  trackByEntry(_: number, t: ZeitEintrag): number {
    return t.id;
  }

  get hasMaterials(): boolean {
    return this.materialTotals.length > 0;
  }

  // ── Material-Freigabe (analog zu den Stunden) ──

  get materialSubmittedCount(): number {
    return this.assignments.reduce(
      (sum, e) => sum + (e.materials ?? []).filter(m => (m.status ?? 'Submitted') === 'Submitted').length, 0);
  }

  materialStatusLabel(status?: string): string {
    return (this.materialStatusLabels as Record<string, string>)[status ?? 'Submitted'] ?? (status ?? '');
  }

  approveMaterialLine(m: EinsatzMaterial, event: Event): void {
    event.stopPropagation();
    if (!this.canManage || m.id == null) return;
    this.stammkundeService.approveMaterial(this.auftragId, m.id).subscribe({
      next: () => { this.reloadAssignments(); this.snackBar.open('Material freigegeben', 'OK', { duration: 2500 }); },
      error: err => this.snackBar.open(err?.error?.message || 'Freigabe fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  reopenMaterialLine(m: EinsatzMaterial, event: Event): void {
    event.stopPropagation();
    if (!this.canManage || m.id == null) return;
    this.stammkundeService.reopenMaterial(this.auftragId, m.id).subscribe({
      next: () => { this.reloadAssignments(); this.snackBar.open('Freigabe zurückgenommen', 'OK', { duration: 2500 }); },
      error: err => this.snackBar.open(err?.error?.message || 'Aktion fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  approveAllMaterials(): void {
    if (!this.canManage || this.materialSubmittedCount === 0) return;
    this.stammkundeService.approveAllMaterials(this.auftragId).subscribe({
      next: res => {
        this.snackBar.open(`${res.approved} Materialposten freigegeben`, 'OK', { duration: 3000 });
        this.reloadAssignments();
      },
      error: err => this.snackBar.open(err?.error?.message || 'Freigabe fehlgeschlagen', 'OK', { duration: 4000 })
    });
  }

  /** Kurzzusammenfassung des Materials für die Einsatz-Karte (max. 2 Positionen). */
  materialSummary(e: AuftragAssignment): string {
    const list = e.materials ?? [];
    const shown = list.slice(0, 2).map(m => `${m.quantity} ${m.unit} ${m.name}`);
    const more = list.length - shown.length;
    return shown.join(', ') + (more > 0 ? ` +${more}` : '');
  }

  trackByDay(_: number, g: { day: Date }): number {
    return g.day.getTime();
  }

  trackByEinsatz(_: number, e: AuftragAssignment): number {
    return e.id;
  }

  editAuftrag(): void {
    if (!this.auftrag) return;
    this.stammkundeService.getLinkedWarehouses(this.customerId).subscribe({
      next: warehouses => {
        const dialogRef = this.dialog.open(AuftragFormComponent, {
          width: '760px',
          maxWidth: '96vw',
          data: {
            customerId: this.customerId,
            auftrag: this.auftrag,
            linkedWarehouses: warehouses ?? [],
            isTakeda: this.isTakedaAuftrag
          }
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.auftrag = result;
            this.snackBar.open('Auftrag aktualisiert', 'OK', { duration: 3000 });
          }
        });
      },
      error: () => this.snackBar.open('Lager konnten nicht geladen werden', 'OK', { duration: 4000 })
    });
  }

  openEinsatzForm(assignment?: AuftragAssignment): void {
    const dialogRef = this.dialog.open(EinsatzFormComponent, {
      width: '640px',
      maxWidth: '96vw',
      data: {
        auftrag: this.auftrag,
        assignment,
        users: this.users,
        workers: this.workers
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.reloadAssignments();
        this.snackBar.open(assignment ? 'Einsatz aktualisiert' : 'Einsatz geplant', 'OK', { duration: 3000 });
      }
    });
  }

  deleteAssignment(assignment: AuftragAssignment, event: Event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Einsatz löschen',
        message: assignment.inviteSent
          ? 'Einsatz wirklich löschen? Es wurde bereits eine Kalender-Einladung verschickt – eine Absage wird mitgesendet.'
          : 'Einsatz wirklich löschen?'
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.stammkundeService
          .deleteAssignment(this.auftragId, assignment.id, assignment.inviteSent)
          .subscribe({
            next: () => {
              this.reloadAssignments();
              this.snackBar.open('Einsatz gelöscht', 'OK', { duration: 3000 });
            },
            error: () => this.snackBar.open('Fehler beim Löschen', 'OK', { duration: 4000 })
          });
      }
    });
  }

  private reloadAssignments(): void {
    this.stammkundeService.getAssignments(this.auftragId).subscribe({
      next: assignments => {
        this.assignments = assignments ?? [];
        this.rebuildDayGroups();
      },
      error: () => { /* Liste bleibt unverändert */ }
    });

    // Auftrag neu laden (Status kann durch Planung auf "Geplant" springen).
    this.stammkundeService.getAuftrag(this.auftragId).subscribe({
      next: auftrag => this.auftrag = auftrag,
      error: () => { /* Anzeige bleibt unverändert */ }
    });
  }

  workStatusLabel(status: string): string {
    switch (status) {
      case 'Planned': return 'Geplant';
      case 'Confirmed': return 'Bestätigt';
      case 'EnRoute': return 'Unterwegs';
      case 'Done': return 'Erledigt';
      default: return status;
    }
  }

  openPlanung(): void {
    this.router.navigate(['/stammkunden', this.customerId, 'planung']);
  }

  back(): void {
    this.router.navigate(['/stammkunden', this.customerId]);
  }
}
